import type { DayKey, DaySchedule } from "@/mocks/branch/settings";
import ToggleSwitch from "@/pages/branch/menu/components/ToggleSwitch";
import { WEEK_DAYS, isOvernight } from "../constants";

interface WorkingHoursSectionProps {
  workingHours: Record<DayKey, DaySchedule>;
  onToggleDay: (day: DayKey, enabled: boolean) => void;
  onChangeTime: (day: DayKey, field: "open" | "close", value: string) => void;
  invalidDays?: DayKey[];
}

export default function WorkingHoursSection({
  workingHours,
  onToggleDay,
  onChangeTime,
  invalidDays = [],
}: WorkingHoursSectionProps) {
  return (
    <section className="bg-background-50 rounded-card border border-background-200 overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-background-200">
        <div className="w-9 h-9 rounded-btn bg-primary-100 flex items-center justify-center text-primary-600">
          <i className="ri-time-line text-xl"></i>
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-bold text-foreground-900">أوقات العمل</h2>
          <p className="text-xs text-foreground-500 mt-0.5">
            حدد أيام العمل وساعات الفتح والإغلاق لكل يوم
          </p>
        </div>
      </div>

      <div className="px-5 pt-4">
        <div className="flex items-start gap-2.5 px-3.5 py-3 rounded-btn bg-secondary-50 border border-secondary-200">
          <i className="ri-information-line text-base text-secondary-600 mt-0.5 shrink-0"></i>
          <p className="text-xs text-secondary-700 leading-relaxed">
            يدعم النظام ساعات العمل الممتدة لما بعد منتصف الليل: إذا كان وقت الإغلاق
            أصغر من وقت الفتح يُعتبر الإغلاق في اليوم التالي.
          </p>
        </div>
      </div>

      <div className="divide-y divide-background-200 mt-4">
        {WEEK_DAYS.map((day) => {
          const schedule = workingHours[day.key];
          const enabled = schedule.enabled;
          const invalid = invalidDays.includes(day.key);
          const overnight = enabled && isOvernight(schedule.open, schedule.close);
          const timeClass = `h-11 px-3 rounded-btn border text-sm text-foreground-900 outline-none transition-colors disabled:opacity-45 disabled:cursor-not-allowed cursor-pointer ${
            invalid
              ? "border-red-300 bg-red-50 focus:border-red-400 focus:ring-2 focus:ring-red-100"
              : "border-background-300 bg-background-50 focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
          }`;

          return (
            <div
              key={day.key}
              className={`flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-3.5 transition-colors ${
                enabled ? "" : "bg-background-100"
              }`}
            >
              <div className="flex items-center gap-3 sm:flex-1 min-w-0">
                <span
                  className={`text-sm font-bold whitespace-nowrap ${
                    enabled ? "text-foreground-900" : "text-foreground-400"
                  }`}
                >
                  {day.label}
                </span>
                <span
                  className={`h-2 w-2 rounded-full shrink-0 ${
                    enabled ? "bg-primary-500" : "bg-background-400"
                  }`}
                ></span>
                {overnight ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-semibold whitespace-nowrap">
                    <i className="ri-moon-line text-xs"></i>
                    يمتد لليوم التالي
                  </span>
                ) : null}
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-foreground-500 whitespace-nowrap">
                    يفتح
                  </span>
                  <input
                    type="time"
                    value={schedule.open}
                    disabled={!enabled}
                    onChange={(e) => onChangeTime(day.key, "open", e.target.value)}
                    className={timeClass}
                  />
                </div>

                <i className="ri-arrow-left-line text-foreground-400 text-lg"></i>

                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-foreground-500 whitespace-nowrap">
                    يغلق
                  </span>
                  <input
                    type="time"
                    value={schedule.close}
                    disabled={!enabled}
                    onChange={(e) => onChangeTime(day.key, "close", e.target.value)}
                    className={timeClass}
                  />
                </div>

                <div className="flex items-center gap-2.5 pr-1">
                  <ToggleSwitch
                    checked={enabled}
                    onChange={() => onToggleDay(day.key, !enabled)}
                    label={`${enabled ? "تعطيل" : "تفعيل"} يوم ${day.label}`}
                  />
                  <span
                    className={`text-xs font-bold whitespace-nowrap w-10 ${
                      enabled ? "text-primary-600" : "text-foreground-400"
                    }`}
                  >
                    {enabled ? "مفتوح" : "مغلق"}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}